/*
 * Locks the grind option when a K-Cup format is selected.
 *
 * Mountain Gap's coffees model K-Cups inconsistently: on most products "K-Cup Pods" is a
 * value of the Size option while grind (Whole Bean / Ground) is a separate option; on one
 * product K-Cup is a value of the grind/format option itself. K-Cups only ship pre-ground,
 * so this element:
 *   - detects a K-Cup selection by option VALUE (robust to which option carries it),
 *   - when the grind option is SEPARATE from the K-Cup-bearing option, forces the only
 *     valid grind (so the variant resolves), dims that option, and shows an explainer note,
 *   - does nothing for products without a grind option (canister, gift card) or without a
 *     K-Cup, and nothing when K-Cup is itself a value of the grind option.
 */
if (!customElements.get('coffee-grind-availability')) {
  customElements.define(
    'coffee-grind-availability',
    class CoffeeGrindAvailability extends HTMLElement {
      connectedCallback() {
        this.variantSelects = document.querySelector(`variant-selects[data-section="${this.dataset.section}"]`);
        if (!this.variantSelects) return;

        this.note = null;
        this.onChange = this.evaluate.bind(this);
        this.variantSelects.addEventListener('change', this.onChange);
        this.evaluate();
      }

      disconnectedCallback() {
        if (this.variantSelects) this.variantSelects.removeEventListener('change', this.onChange);
      }

      static isKcup(value) {
        return typeof value === 'string' && value.toLowerCase().replace(/[\s-]/g, '').includes('kcup');
      }

      static isGrindValue(value) {
        const normalized = (value || '').toLowerCase();
        return normalized.includes('whole bean') || normalized.includes('ground');
      }

      groups() {
        return Array.from(this.variantSelects.querySelectorAll('.product-form__input'))
          .map((wrapper) => {
            const radios = Array.from(wrapper.querySelectorAll('input[type="radio"]'));
            if (radios.length) {
              const checked = radios.find((radio) => radio.checked);
              return {
                wrapper,
                radios,
                values: radios.map((radio) => radio.value),
                selected: checked ? checked.value : null,
              };
            }

            const select = wrapper.querySelector('select');
            if (select) {
              return {
                wrapper,
                radios: null,
                values: Array.from(select.options, (option) => option.value),
                selected: select.value,
              };
            }

            return null;
          })
          .filter(Boolean);
      }

      evaluate() {
        const groups = this.groups();
        if (!groups.length) return;

        const grindGroup = groups.find((group) =>
          group.values.some((value) => CoffeeGrindAvailability.isGrindValue(value)),
        );
        const kcupGroup = groups.find((group) => CoffeeGrindAvailability.isKcup(group.selected));
        const lock = Boolean(kcupGroup) && Boolean(grindGroup) && grindGroup !== kcupGroup;

        if (lock) {
          this.forceAvailableGrind(grindGroup);
          grindGroup.wrapper.classList.add('is-format-locked');
          grindGroup.wrapper.setAttribute('aria-disabled', 'true');
          this.showNote(grindGroup.wrapper);
        } else if (grindGroup) {
          grindGroup.wrapper.classList.remove('is-format-locked');
          grindGroup.wrapper.removeAttribute('aria-disabled');
          this.hideNote();
        }
      }

      // If the checked grind is not valid for the K-Cup variant, switch to the one that is
      // and let Dawn recompute the variant. Terminates: once a valid grind is checked, the
      // guard below is satisfied and no further change event is dispatched.
      forceAvailableGrind(grindGroup) {
        if (!grindGroup.radios) return;

        const checked = grindGroup.radios.find((radio) => radio.checked);
        if (checked && !checked.classList.contains('disabled')) return;

        const target = grindGroup.radios.find((radio) => !radio.classList.contains('disabled'));
        if (target && target !== checked) {
          target.checked = true;
          target.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }

      showNote(anchor) {
        if (!this.dataset.note) return;

        if (!this.note) {
          this.note = document.createElement('p');
          this.note.className = 'coffee-format-note';
          this.note.setAttribute('role', 'note');
          this.note.textContent = this.dataset.note;
        }

        if (this.note.previousElementSibling !== anchor) {
          anchor.insertAdjacentElement('afterend', this.note);
        }
        this.note.hidden = false;
      }

      hideNote() {
        if (this.note) this.note.hidden = true;
      }
    },
  );
}
